import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef } from 'react';
import { useTheme } from '../../theme';
export const Modal = forwardRef(({ open, onClose, size = 'md', closeOnBackdropClick = true, closeOnEscape = true, centered = true, className, style, children, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, }, ref) => {
    const theme = useTheme();
    const modalRef = useRef(null);
    const previousFocusRef = useRef(null);
    // Handle escape key
    useEffect(() => {
        if (!open || !closeOnEscape)
            return;
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [open, closeOnEscape, onClose]);
    // Focus management
    useEffect(() => {
        if (open) {
            previousFocusRef.current = document.activeElement;
            modalRef.current?.focus();
        }
        else {
            previousFocusRef.current?.focus();
        }
    }, [open]);
    // Prevent body scroll when modal is open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [open]);
    if (!open)
        return null;
    const getModalSize = () => {
        const sizeStyles = {
            sm: {
                maxWidth: '400px',
                width: '90vw',
            },
            md: {
                maxWidth: '500px',
                width: '90vw',
            },
            lg: {
                maxWidth: '700px',
                width: '90vw',
            },
            xl: {
                maxWidth: '900px',
                width: '90vw',
            },
            full: {
                width: '95vw',
                height: '95vh',
                maxWidth: 'none',
                maxHeight: 'none',
            },
        };
        return sizeStyles[size];
    };
    const backdropStyles = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: centered ? 'center' : 'flex-start',
        justifyContent: 'center',
        padding: theme.spacing.md,
        zIndex: 1000,
        opacity: open ? 1 : 0,
        transition: 'opacity 0.2s ease',
    };
    const modalStyles = {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        maxHeight: '90vh',
        overflow: 'auto',
        outline: 'none',
        transform: open ? 'scale(1)' : 'scale(0.95)',
        transition: 'transform 0.2s ease',
        ...getModalSize(),
        ...style,
    };
    return (_jsx("div", { style: backdropStyles, onClick: (e) => {
            if (closeOnBackdropClick && e.target === e.currentTarget) {
                onClose();
            }
        }, children: _jsx("div", { ref: ref || modalRef, className: className, style: modalStyles, tabIndex: -1, role: "dialog", "aria-modal": "true", ...(ariaLabel && { 'aria-label': ariaLabel }), ...(ariaLabelledBy && { 'aria-labelledby': ariaLabelledBy }), children: children }) }));
});
Modal.displayName = 'Modal';
export const ModalHeader = forwardRef(({ showCloseButton = true, onClose, className, style, children }, ref) => {
    const theme = useTheme();
    return (_jsxs("div", { ref: ref, className: className, style: {
            padding: `${theme.spacing.lg} ${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.lg}`,
            borderBottom: `1px solid ${theme.colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            ...style,
        }, children: [_jsx("div", { style: { flex: 1 }, children: children }), showCloseButton && onClose && (_jsx("button", { onClick: onClose, style: {
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    padding: theme.spacing.xs,
                    marginLeft: theme.spacing.md,
                    borderRadius: theme.borderRadius.sm,
                    color: theme.colors.textSecondary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }, "aria-label": "Close modal", children: "\u00D7" }))] }));
});
ModalHeader.displayName = 'ModalHeader';
export const ModalBody = forwardRef(({ className, style, children }, ref) => {
    const theme = useTheme();
    return (_jsx("div", { ref: ref, className: className, style: {
            padding: theme.spacing.lg,
            flex: 1,
            ...style,
        }, children: children }));
});
ModalBody.displayName = 'ModalBody';
export const ModalFooter = forwardRef(({ className, style, children }, ref) => {
    const theme = useTheme();
    return (_jsx("div", { ref: ref, className: className, style: {
            padding: `${theme.spacing.md} ${theme.spacing.lg} ${theme.spacing.lg} ${theme.spacing.lg}`,
            borderTop: `1px solid ${theme.colors.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: theme.spacing.sm,
            ...style,
        }, children: children }));
});
ModalFooter.displayName = 'ModalFooter';
